// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState } from 'react';
import { Spinner } from '@loa/ui-components';
import { InvitationEmailEditor } from '../../invitation/InvitationEmailEditor';
import type { EmailTemplate } from '../../../types/invitation';
import './completion.css';

/**
 * SVG Icons
 */
const Icons = {
  chevronDown: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6,9 12,15 18,9" />
    </svg>
  ),
  mail: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  info: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  ),
  warning: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
};

export interface InvitationSectionProps {
  /**
   * Number of clients ready to receive invitations
   */
  clientCount: number;
  /**
   * Default email template from system
   */
  defaultTemplate: EmailTemplate | null;
  /**
   * Whether the template is still loading
   */
  templateLoading: boolean;
  /**
   * Current subject line
   */
  subject: string;
  /**
   * Current body HTML
   */
  body: string;
  /**
   * Whether the email has been modified from default
   */
  isModified: boolean;
  /**
   * Callback when subject changes
   */
  onSubjectChange: (subject: string) => void;
  /**
   * Callback when body changes
   */
  onBodyChange: (body: string) => void;
  /**
   * Callback to reset to default template
   */
  onReset: () => void;
  /**
   * Whether the section is disabled (e.g., during sending)
   */
  disabled?: boolean;
}

/**
 * InvitationSection - Collapsible section for email customization
 *
 * Features:
 * - Collapsed by default with summary text
 * - Expands to show InvitationEmailEditor
 * - Shows loading state while template loads
 * - Handles template load failures gracefully
 */
export const InvitationSection = ({
  clientCount,
  defaultTemplate,
  templateLoading,
  subject,
  body,
  isModified,
  onSubjectChange,
  onBodyChange,
  onReset,
  disabled = false,
}: InvitationSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const clientText = clientCount === 1 ? '1 client' : `${clientCount} clients`;
  const summaryText = `${clientText} ready to receive invitation emails`;

  return (
    <div className="wizard-step-card" style={{ marginTop: '1.5rem' }}>
      {/* Collapsible Header */}
      <div
        className={`wizard-collapsible__header ${isExpanded ? 'wizard-collapsible__header--open' : ''}`}
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ borderRadius: isExpanded ? '12px 12px 0 0' : '12px' }}
      >
        <span className={`wizard-collapsible__icon ${isExpanded ? 'wizard-collapsible__icon--open' : ''}`}>
          {Icons.chevronDown}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ width: '18px', height: '18px' }}>{Icons.mail}</span>
          {' '}Customize Invitation Email
        </span>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="wizard-step-card__body">
          {templateLoading ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <Spinner />
            </div>
          ) : !defaultTemplate ? (
            <div className="wizard-info-box wizard-info-box--warning">
              <div className="wizard-info-box__content">
                <span className="wizard-info-box__icon" style={{ width: '20px', height: '20px' }}>{Icons.warning}</span>
                <div className="wizard-info-box__text">
                  <strong>Template Unavailable</strong>
                  <br />
                  Could not load the email template. You can still send invitations
                  with the default system template.
                </div>
              </div>
            </div>
          ) : (
            <InvitationEmailEditor
              subject={subject}
              body={body}
              onSubjectChange={onSubjectChange}
              onBodyChange={onBodyChange}
              disabled={disabled}
              isModified={isModified}
              onReset={onReset}
            />
          )}
        </div>
      )}

      {/* Summary text shown when collapsed */}
      {!isExpanded && (
        <div style={{ padding: '0.75rem 1rem', borderTop: '1px solid #e0e0e0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#666', fontSize: '14px' }}>
            <span style={{ width: '16px', height: '16px' }}>{Icons.info}</span>
            {summaryText}
          </div>
        </div>
      )}
    </div>
  );
};

export default InvitationSection;
